"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth } from "./use-auth"

export interface ShopifyIntegration {
  id: string
  user_id: string
  shop_domain: string
  uses_webhooks: boolean
  uses_api: boolean
  webhook_secret?: string
  api_access_token_enc?: string
  is_active: boolean
  /**
   * Bumped by `createRulesDal.onWriteSuccess` on every successful
   * bundle-rule or family-rule write. Read by the staleness pill via
   * `isRecomputeStale`. Null when no rule edits have bumped it.
   */
  bundle_rules_changed_at: string | null
  created_at: string
  updated_at: string
}

export interface ShopifyOrder {
  integration_id: string
  shopify_order_id: string
  name: string
  created_at_shopify: string
  updated_at_shopify: string
  currency: string
  total_price: number
  financial_status: string
  fulfillment_status: string
  customer_id: string
  customer_email: string
  customer_first_name: string
  customer_last_name: string
  raw_json: unknown
  inserted_at: string
}

const DEFAULT_ORDERS_PAGE_SIZE = 25

export type OrderSortField = "name" | "total_price" | "financial_status" | "created_at_shopify"
export type SortOrder = "asc" | "desc"

interface UseShopifyOptions {
  monthStartUtc?: string | null
  monthEndUtc?: string | null
}

export function useShopify(options?: UseShopifyOptions) {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [integrations, setIntegrations] = useState<ShopifyIntegration[]>([])
  const [orders, setOrders] = useState<ShopifyOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ordersError, setOrdersError] = useState<string | null>(null)
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersPageSize, setOrdersPageSize] = useState(DEFAULT_ORDERS_PAGE_SIZE)
  const [ordersTotal, setOrdersTotal] = useState(0)
  const [ordersSearch, setOrdersSearch] = useState("")
  const [ordersSortBy, setOrdersSortBy] = useState<OrderSortField>("created_at_shopify")
  const [ordersSortOrder, setOrdersSortOrder] = useState<SortOrder>("desc")

  const monthStartUtc = options?.monthStartUtc ?? null
  const monthEndUtc = options?.monthEndUtc ?? null

  // Filter only active integrations for use in UI components (dashboard, orders, etc.)
  const activeIntegrations = integrations.filter((i) => i.is_active)

  // Check if there's at least one active integration
  const hasIntegration = useCallback(() => activeIntegrations.length > 0, [activeIntegrations])

  const loadIntegrations = useCallback(async () => {
    if (!isAuthenticated) {
      setIntegrations([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const response = await fetchWithAuth(`/api/integrations/shopify`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        setIntegrations(data.integrations || [])
        setError(null)
      } else if (response.status === 404) {
        setIntegrations([])
        setError(null)
      } else {
        setIntegrations([])
      }
    } catch (_error) {
      setIntegrations([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated])

  const loadOrders = useCallback(
    async (
      requestedPage: number,
      requestedPageSize: number,
      search?: string,
      sortBy?: OrderSortField,
      sortOrder?: SortOrder,
    ) => {
      if (!isAuthenticated) {
        setOrders([])
        setOrdersTotal(0)
        setOrdersPage(1)
        setOrdersPageSize(clampOrdersPageSize(requestedPageSize, DEFAULT_ORDERS_PAGE_SIZE))
        return
      }

      const pageSize = clampOrdersPageSize(requestedPageSize, DEFAULT_ORDERS_PAGE_SIZE)
      const searchQuery = search ?? ordersSearch
      const effectiveSortBy = sortBy ?? ordersSortBy
      const effectiveSortOrder = sortOrder ?? ordersSortOrder

      if (integrations.length === 0) {
        setOrders([])
        setOrdersTotal(0)
        setOrdersPage(1)
        setOrdersPageSize(pageSize)
        return
      }

      const page = Math.max(1, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1)

      setOrdersLoading(true)
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        })
        if (searchQuery) {
          params.set("search", searchQuery)
        }
        if (monthStartUtc) params.set("month_start_utc", monthStartUtc)
        if (monthEndUtc) params.set("month_end_utc", monthEndUtc)
        params.set("sort_by", effectiveSortBy)
        params.set("sort_order", effectiveSortOrder)

        const response = await fetchWithAuth(`/api/integrations/shopify/orders?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })

        if (response.ok) {
          const data = await response.json()
          setOrders(data.orders || [])
          setOrdersError(null)
          setOrdersTotal(data.total ?? 0)
          setOrdersPage(data.page ?? page)
          setOrdersPageSize(data.pageSize ?? pageSize)
        } else {
          setOrders([])
          setOrdersError(null)
          setOrdersTotal(0)
          setOrdersPage(page)
          setOrdersPageSize(pageSize)
        }
      } catch (_error) {
        setOrders([])
        setOrdersError(null)
        setOrdersTotal(0)
        setOrdersPage(page)
        setOrdersPageSize(pageSize)
      } finally {
        setOrdersLoading(false)
      }
    },
    [fetchWithAuth, isAuthenticated, integrations, ordersSearch, ordersSortBy, ordersSortOrder, monthStartUtc, monthEndUtc],
  )

  const saveActiveDomains = useCallback(
    async (activeDomains: string[]) => {
      if (!isAuthenticated) {
        return false
      }

      try {
        const response = await fetchWithAuth(`/api/integrations/shopify`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            domains: activeDomains,
          }),
        })

        if (response.ok) {
          const data = await response.json()
          setIntegrations(data.integrations || [])
          return true
        } else {
          return false
        }
      } catch (_error) {
        return false
      }
    },
    [fetchWithAuth, isAuthenticated],
  )

  useEffect(() => {
    void loadIntegrations()
  }, [loadIntegrations])

  // Re-fetch when integrations change (connect/disconnect/reconnect from another hook)
  useEffect(() => {
    const handler = () => void loadIntegrations()
    window.addEventListener('shopify-integrations-changed', handler)
    return () => window.removeEventListener('shopify-integrations-changed', handler)
  }, [loadIntegrations])

  // Re-fetch when tab becomes visible (covers OAuth popup close, tab switch, etc.)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        void loadIntegrations()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [loadIntegrations])

  useEffect(() => {
    if (integrations.length > 0) {
      void loadOrders(1, DEFAULT_ORDERS_PAGE_SIZE)
    } else {
      setOrders([])
      setOrdersTotal(0)
      setOrdersPage(1)
      setOrdersPageSize(DEFAULT_ORDERS_PAGE_SIZE)
    }
  }, [integrations, loadOrders])

  const refreshIntegrations = useCallback(() => loadIntegrations(), [loadIntegrations])

  const refreshOrders = useCallback(() => loadOrders(ordersPage, ordersPageSize), [loadOrders, ordersPage, ordersPageSize])

  const goToOrdersPage = useCallback((page: number) => loadOrders(page, ordersPageSize), [loadOrders, ordersPageSize])

  const changeOrdersPageSize = useCallback((pageSize: number) => loadOrders(1, pageSize), [loadOrders])

  const searchOrders = useCallback((search: string) => {
    setOrdersSearch(search)
    loadOrders(1, ordersPageSize, search)
  }, [loadOrders, ordersPageSize])

  const clearOrdersSearch = useCallback(() => {
    setOrdersSearch("")
    loadOrders(1, ordersPageSize, "")
  }, [loadOrders, ordersPageSize])

  const sortOrders = useCallback((column: OrderSortField) => {
    const newOrder: SortOrder = column === ordersSortBy
      ? (ordersSortOrder === "asc" ? "desc" : "asc")
      : "desc"
    setOrdersSortBy(column)
    setOrdersSortOrder(newOrder)
    loadOrders(1, ordersPageSize, undefined, column, newOrder)
  }, [ordersSortBy, ordersSortOrder, ordersPageSize, loadOrders])

  return {
    integrations,         // All integrations (for management UI)
    activeIntegrations,   // Only active integrations (for dashboard, orders, etc.)
    orders,
    ordersPage,
    ordersPageSize,
    ordersTotal,
    ordersSearch,
    ordersSortBy,
    ordersSortOrder,
    isLoading,
    ordersLoading,
    error,
    ordersError,
    hasIntegration,
    refreshIntegrations,
    refreshOrders,
    goToOrdersPage,
    changeOrdersPageSize,
    searchOrders,
    clearOrdersSearch,
    sortOrders,
    saveActiveDomains,
  }
}

function clampOrdersPageSize(value: number, fallback: number) {
  const size = Number(value)
  if (!Number.isFinite(size) || size <= 0) {
    return fallback
  }
  return Math.min(Math.max(Math.floor(size), 1), 100)
}
